import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIRST_SPAWN_MS = 25000;
const SPAWN_EVERY_MS = [150000, 300000]; // 2.5–5 min

// 🎈 Wandering Shape Balloons — every now and then a random custom shape drifts by.
// Free ad for the Shape Shop + tappable Shape Hunt credit for drivers (3/day).
export const WanderBalloon = () => {
  const [shapes, setShapes] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [flying, setFlying] = useState(null);
  const [popped, setPopped] = useState(false);
  const [note, setNote] = useState(null);
  const timer = useRef(null);
  const clearT = useRef(null);

  useEffect(() => {
    axios.get(`${API}/balloon-ads/shape-catalog`).then((r) => setShapes(r.data.shapes || [])).catch(() => {});
    axios.get(`${API}/balloon-ads/shape-spotlight`).then((r) => setSpotlight(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (shapes.length === 0) return undefined;
    const spawn = () => {
      const roll = Math.random();
      let s;
      if (roll < 0.08) {
        s = { id: "golden_pogzilla", emoji: "🦖", label: "GOLDEN POGZILLA", golden: true };
      } else if (spotlight && roll < 0.38) {
        s = { id: spotlight.shape, emoji: spotlight.emoji, label: spotlight.label };
      } else {
        s = shapes[Math.floor(Math.random() * shapes.length)];
      }
      const dur = 30 + Math.random() * 12;
      setPopped(false);
      setFlying({ ...s, key: Date.now(), top: 14 + Math.random() * 30, dur });
      clearTimeout(clearT.current);
      clearT.current = setTimeout(() => setFlying(null), (dur + 2) * 1000); // fully unmount off-screen balloon
      const next = SPAWN_EVERY_MS[0] + Math.random() * (SPAWN_EVERY_MS[1] - SPAWN_EVERY_MS[0]);
      timer.current = setTimeout(spawn, next);
    };
    timer.current = setTimeout(spawn, FIRST_SPAWN_MS);
    return () => { clearTimeout(timer.current); clearTimeout(clearT.current); };
  }, [shapes, spotlight]);

  const toast = (msg) => {
    setNote(msg);
    setTimeout(() => setNote(null), 3000);
  };

  const tap = async (s) => {
    if (popped) return;
    setPopped(true);
    setTimeout(() => setFlying(null), 450);
    try {
      const r = await axios.post(`${API}/shape-hunt/wander-tap`, { shape: s.id }, { withCredentials: true });
      if (r.data.golden) {
        toast(r.data.golden_taken ? "The GOLDEN POGZILLA already blessed you today! 🌟" : `🌟🦖 GOLDEN POGZILLA! +${r.data.bucks_awarded} POG BUCKS!`);
      } else if (r.data.capped) toast(`The wild ${s.label} waves back — hunt credit maxed for today! 🎈`);
      else if (r.data.hunt_reward) toast(`🎯 SHAPE HUNT COMPLETE! +${r.data.hunt_reward} POG BUCKS! 🏆`);
      else toast(`Wild ${s.label} tagged! Shape Hunt ${r.data.hunt_progress}/${r.data.hunt_target} 🎯`);
    } catch {
      toast("Log in free to count wild shapes toward your Shape Hunt! 🎯");
    }
  };

  return (
    <>
      <AnimatePresence>
        {flying && (
          <motion.button
            key={flying.key}
            data-testid="wander-balloon"
            initial={{ x: "-12vw", opacity: 0 }}
            animate={popped
              ? { scale: [1, 1.5, 0], opacity: [1, 1, 0], transition: { duration: 0.45 } }
              : { x: "112vw", opacity: 1, y: [0, -14, 10, -8, 0], transition: { x: { duration: flying.dur, ease: "linear" }, y: { duration: 7, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.8 } } }}
            exit={{ opacity: 0 }}
            onClick={() => tap(flying)}
            className="fixed z-[1240] pointer-events-auto cursor-pointer bg-transparent border-0"
            style={{ top: `${flying.top}%`, left: 0 }}
            title={`Wild ${flying.label} balloon!`}
          >
            <span className="block leading-none animate-pulse" data-testid={flying.golden ? "golden-pogzilla" : undefined} style={{ fontSize: flying.golden ? 54 : 44, filter: flying.golden ? "drop-shadow(0 0 20px rgba(251,191,36,0.95)) sepia(1) saturate(3) hue-rotate(-15deg)" : "drop-shadow(0 0 14px rgba(56,189,248,0.65))" }}>
              {flying.emoji}
            </span>
            {flying.golden && <span className="absolute -top-2 -right-2 text-base animate-ping">✨</span>}
            <svg viewBox="0 0 10 22" style={{ width: 8, height: 16, margin: "0 auto" }}>
              <path d="M5 0 Q3 8 5 14 Q7 19 5 22" stroke="#cbd5e1" strokeWidth="1.2" fill="none" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {note && (
          <motion.div
            data-testid="wander-toast"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="fixed bottom-[calc(9rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[2300] bg-slate-900/95 border border-sky-400/40 rounded-full px-4 py-2 text-xs font-bold text-sky-100 shadow-xl whitespace-nowrap"
          >
            {note}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
